import type { CharacterLayerStatus } from '../types/characterLayerTypes'
import type { CoreferenceChainV2FigureKind as CharacterIndexFigureKind } from '@variorum/character-index'
import type {
  ChapterCharacterLayer,
  CharacterEntity,
  CharacterLayerNote,
  CharacterLayerNoteAnchor,
  CharacterOccurrence,
  CharacterOccurrenceLocator,
} from '../types/characterLayerTypes'

export const CHARACTER_LAYER_VERSION = 1

const nowIso = (): string => new Date().toISOString()

const stableSuffix = (): string => Math.random().toString(36).slice(2, 10)

const createId = (prefix: string): string => `${prefix}_${Date.now().toString(36)}_${stableSuffix()}`

const isActiveStatus = (status: CharacterLayerStatus): boolean => status !== 'rejected'

const normalizeRoleLabel = (label: string): string => label.trim().replace(/\s+/g, ' ')

const dedupeStrings = (values: string[]): string[] => Array.from(new Set(values.map(normalizeRoleLabel).filter(Boolean)))

const CHARACTER_FIGURE_KINDS = new Set<CharacterIndexFigureKind>(['person', 'animal', 'being', 'group'])

export const normalizeCharacterFigureKind = (value: unknown): CharacterIndexFigureKind | undefined => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return CHARACTER_FIGURE_KINDS.has(normalized as CharacterIndexFigureKind)
    ? normalized as CharacterIndexFigureKind
    : undefined
}

export const getCharacterFigureKind = (
  character: Pick<CharacterEntity, 'kind' | 'metadata'>,
): CharacterIndexFigureKind | undefined => (
  normalizeCharacterFigureKind(character.kind) ||
  normalizeCharacterFigureKind(character.metadata?.figure_kind) ||
  normalizeCharacterFigureKind(character.metadata?.figureKind) ||
  normalizeCharacterFigureKind(character.metadata?.kind)
)

export const getCharacterOccurrenceReferentIds = (
  occurrence: Pick<CharacterOccurrence, 'character_id' | 'referent_character_ids'>,
): string[] => Array.from(new Set([
  occurrence.character_id,
  ...(Array.isArray(occurrence.referent_character_ids) ? occurrence.referent_character_ids : []),
].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)))

export const characterOccurrenceRefersTo = (
  occurrence: Pick<CharacterOccurrence, 'character_id' | 'referent_character_ids'>,
  characterId: string,
): boolean => getCharacterOccurrenceReferentIds(occurrence).includes(characterId)

export const isJointCharacterOccurrence = (
  occurrence: Pick<CharacterOccurrence, 'character_id' | 'referent_character_ids'>,
): boolean => getCharacterOccurrenceReferentIds(occurrence).length > 1

export const createEmptyChapterCharacterLayer = ({
  chapterId,
  bookId,
  createdAt,
}: {
  chapterId: string
  bookId: string
  createdAt?: string
}): ChapterCharacterLayer => {
  const timestamp = createdAt || nowIso()
  return {
    chapter_id: chapterId,
    book_id: bookId,
    version: CHARACTER_LAYER_VERSION,
    characters: [],
    occurrences: [],
    notes: [],
    created_at: timestamp,
    updated_at: timestamp,
  }
}

export const getActiveCharacters = (
  layer: ChapterCharacterLayer | undefined | null,
): CharacterEntity[] => (
  Array.isArray(layer?.characters)
    ? layer!.characters.filter(character => isActiveStatus(character.status))
    : []
)

export const getActiveCharacterOccurrences = (
  layer: ChapterCharacterLayer | undefined | null,
  characterId?: string,
): CharacterOccurrence[] => {
  const activeCharacterIds = new Set(getActiveCharacters(layer).map(character => character.character_id))
  return Array.isArray(layer?.occurrences)
    ? layer!.occurrences.filter(occurrence => (
      isActiveStatus(occurrence.status) &&
      getCharacterOccurrenceReferentIds(occurrence).some(id => activeCharacterIds.has(id)) &&
      (!characterId || characterOccurrenceRefersTo(occurrence, characterId))
    ))
    : []
}

export const buildCharacterCellKey = (locator: CharacterOccurrenceLocator): string => (
  `${locator.rowId}:${locator.witnessId}`
)

export const areCharacterLocatorsEqual = (
  left: CharacterOccurrenceLocator,
  right: CharacterOccurrenceLocator,
): boolean => (
  left.witnessId === right.witnessId &&
  left.rowId === right.rowId &&
  left.localStart === right.localStart &&
  left.localEnd === right.localEnd
)

export interface CharacterCoverageSummary {
  witnessIds: string[]
  rowIds: string[]
  occurrenceCount: number
  isAllWitnesses: boolean
  label: string
}

export const buildCharacterCoverageSummary = ({
  layer,
  characterId,
  witnessIds,
}: {
  layer: ChapterCharacterLayer | undefined | null
  characterId: string
  witnessIds: string[]
}): CharacterCoverageSummary => {
  const occurrences = getActiveCharacterOccurrences(layer, characterId)
  const witnessSet = new Set(occurrences.map(occurrence => occurrence.locator.witnessId))
  const rowIds = Array.from(new Set(occurrences.map(occurrence => occurrence.locator.rowId))).sort()
  const coveredWitnessIds = witnessIds.filter(witnessId => witnessSet.has(witnessId))
  const isAllWitnesses = witnessIds.length > 0 && coveredWitnessIds.length === witnessIds.length
  return {
    witnessIds: coveredWitnessIds,
    rowIds,
    occurrenceCount: occurrences.length,
    isAllWitnesses,
    label: isAllWitnesses ? 'All witnesses' : `${coveredWitnessIds.length}/${Math.max(1, witnessIds.length)} witnesses`,
  }
}

export const createManualCharacter = ({
  layer,
  displayName,
  kind = 'person',
  aliases = [],
  createdAt,
}: {
  layer: ChapterCharacterLayer
  displayName: string
  kind?: CharacterIndexFigureKind
  aliases?: string[]
  createdAt?: string
}): { layer: ChapterCharacterLayer; character: CharacterEntity } => {
  const timestamp = createdAt || nowIso()
  const character: CharacterEntity = {
    character_id: createId('character'),
    kind,
    display_name: displayName.trim(),
    aliases: dedupeStrings(aliases),
    status: 'confirmed',
    origin: 'manual',
    metadata: { figure_kind: kind },
    created_at: timestamp,
    updated_at: timestamp,
  }
  return {
    character,
    layer: {
      ...layer,
      characters: [...layer.characters, character],
      updated_at: timestamp,
    },
  }
}

export const upsertManualCharacterOccurrence = ({
  layer,
  characterId,
  locator,
  roleLabels = [],
  notes,
  createdAt,
}: {
  layer: ChapterCharacterLayer
  characterId: string
  locator: CharacterOccurrenceLocator
  roleLabels?: string[]
  notes?: string
  createdAt?: string
}): { layer: ChapterCharacterLayer; occurrence: CharacterOccurrence } => {
  const timestamp = createdAt || nowIso()
  const normalizedRoles = dedupeStrings(roleLabels)
  const existingIndex = layer.occurrences.findIndex(occurrence => (
    characterOccurrenceRefersTo(occurrence, characterId) &&
    isActiveStatus(occurrence.status) &&
    areCharacterLocatorsEqual(occurrence.locator, locator)
  ))

  if (existingIndex >= 0) {
    const existing = layer.occurrences[existingIndex]
    const occurrence: CharacterOccurrence = {
      ...existing,
      role_labels: dedupeStrings([...existing.role_labels, ...normalizedRoles]),
      ...(notes ? { notes } : {}),
      updated_at: timestamp,
    }
    const occurrences = [...layer.occurrences]
    occurrences[existingIndex] = occurrence
    return {
      occurrence,
      layer: {
        ...layer,
        occurrences,
        updated_at: timestamp,
      },
    }
  }

  const occurrence: CharacterOccurrence = {
    occurrence_id: createId('character_occurrence'),
    character_id: characterId,
    locator,
    role_labels: normalizedRoles,
    status: 'confirmed',
    origin: 'manual',
    ...(notes ? { notes } : {}),
    created_at: timestamp,
    updated_at: timestamp,
  }
  return {
    occurrence,
    layer: {
      ...layer,
      occurrences: [...layer.occurrences, occurrence],
      updated_at: timestamp,
    },
  }
}

export const appendCharacterLayerNote = ({
  layer,
  anchor,
  text,
  createdAt,
}: {
  layer: ChapterCharacterLayer
  anchor: CharacterLayerNoteAnchor
  text: string
  createdAt?: string
}): { layer: ChapterCharacterLayer; note: CharacterLayerNote } => {
  const timestamp = createdAt || nowIso()
  const note: CharacterLayerNote = {
    note_id: createId('character_note'),
    anchor,
    text: text.trim(),
    created_at: timestamp,
    updated_at: timestamp,
  }
  return {
    note,
    layer: {
      ...layer,
      notes: [...layer.notes, note],
      updated_at: timestamp,
    },
  }
}

export const reviewCharacterEntity = ({
  layer,
  characterId,
  nextStatus,
  reviewer,
  updates,
  reviewedAt,
}: {
  layer: ChapterCharacterLayer
  characterId: string
  nextStatus: Extract<CharacterLayerStatus, 'confirmed' | 'edited' | 'rejected'>
  reviewer: string
  updates?: Partial<Pick<CharacterEntity, 'kind' | 'display_name' | 'aliases' | 'notes' | 'metadata'>>
  reviewedAt?: string
}): ChapterCharacterLayer => {
  const timestamp = reviewedAt || nowIso()
  return {
    ...layer,
    characters: layer.characters.map(character =>
      character.character_id !== characterId
        ? character
        : {
            ...character,
            ...updates,
            status: nextStatus,
            review: {
              reviewed_at: timestamp,
              reviewed_by: reviewer,
            },
            updated_at: timestamp,
          }
    ),
    updated_at: timestamp,
  }
}

/** Removes a character from the working index without erasing the editorial
 * decision. The rejected character remains as a detector-suppression tombstone;
 * sole readings are relegated, while joint occurrences retain their surviving
 * referents and remain editable evidence. */
export const deleteCharacterRecord = ({
  layer,
  characterId,
  reviewer,
  deletedAt,
}: {
  layer: ChapterCharacterLayer
  characterId: string
  reviewer: string
  deletedAt?: string
}): ChapterCharacterLayer => {
  const timestamp = deletedAt || nowIso()
  const review = {
    reviewed_at: timestamp,
    reviewed_by: reviewer,
  }
  const editorialMetadata = (metadata: Record<string, any> | undefined) => ({
    ...(metadata || {}),
    editorial_standing: 'relegated',
    editorial_origin: 'human',
    editorial_review: review,
  })
  const noteTargetsCharacter = (note: CharacterLayerNote): boolean => (
    note.metadata?.character_id === characterId ||
    (note.anchor.kind === 'character' && note.anchor.characterId === characterId)
  )

  return {
    ...layer,
    characters: layer.characters.map(character => (
      character.character_id === characterId
        ? {
            ...character,
            status: 'rejected',
            review,
            updated_at: timestamp,
          }
        : character
    )),
    occurrences: layer.occurrences.map(occurrence => {
      const referents = getCharacterOccurrenceReferentIds(occurrence)
      if (!referents.includes(characterId)) {
        return occurrence
      }
      const survivingReferents = referents.filter(referentId => referentId !== characterId)
      if (survivingReferents.length === 0) {
        return {
          ...occurrence,
          status: 'rejected',
          metadata: editorialMetadata(occurrence.metadata),
          review,
          updated_at: timestamp,
        }
      }
      const nextOccurrence: CharacterOccurrence = {
        ...occurrence,
        character_id: occurrence.character_id === characterId
          ? survivingReferents[0]
          : occurrence.character_id,
        status: 'edited',
        origin: 'manual',
        metadata: { ...(occurrence.metadata || {}) },
        review,
        updated_at: timestamp,
      }
      if (survivingReferents.length > 1) {
        nextOccurrence.referent_character_ids = survivingReferents
        nextOccurrence.metadata = {
          ...nextOccurrence.metadata,
          reference_mode: 'joint',
        }
      } else {
        delete nextOccurrence.referent_character_ids
        delete nextOccurrence.metadata?.reference_mode
      }
      return nextOccurrence
    }),
    notes: layer.notes.map(note => (
      noteTargetsCharacter(note)
        ? {
            ...note,
            metadata: editorialMetadata(note.metadata),
            updated_at: timestamp,
          }
        : note
    )),
    updated_at: timestamp,
  }
}

export const reviewCharacterOccurrence = ({
  layer,
  occurrenceId,
  nextStatus,
  reviewer,
  updates,
  reviewedAt,
}: {
  layer: ChapterCharacterLayer
  occurrenceId: string
  nextStatus: Extract<CharacterLayerStatus, 'confirmed' | 'edited' | 'rejected'>
  reviewer: string
  updates?: Partial<Pick<CharacterOccurrence, 'locator' | 'role_labels' | 'label' | 'summary' | 'notes' | 'metadata'>>
  reviewedAt?: string
}): ChapterCharacterLayer => {
  const timestamp = reviewedAt || nowIso()
  return {
    ...layer,
    occurrences: layer.occurrences.map(occurrence =>
      occurrence.occurrence_id !== occurrenceId
        ? occurrence
        : {
            ...occurrence,
            ...updates,
            status: nextStatus,
            review: {
              reviewed_at: timestamp,
              reviewed_by: reviewer,
            },
            updated_at: timestamp,
          }
    ),
    updated_at: timestamp,
  }
}
